import {useCallback, useEffect, useMemo, useState} from 'react'
import {
  useTable,
  UseSortByColumnProps,
  ColumnInstance,
  Row,
  useSortBy,
  TableInstance,
} from 'react-table'
import {KTCard, KTCardBody, useDebounce} from '../../../../_metronic/helpers'
import {Loader} from '../loader'
import {getCategoryColumns} from './columns'
import {ProductCategory, getCategories} from './api'
import clsx from 'clsx'
import {useLayout} from '../../../../_metronic/layout/core'
import {KTIcon} from '../../../../_metronic/helpers'
import {CreateEditCategory} from './create-edit-category'
import {Modal} from 'react-bootstrap'

// ─── Types ────────────────────────────────────────────────────────────────────

type SortableColumn = ColumnInstance<ProductCategory> & UseSortByColumnProps<ProductCategory>
type TableInstanceWithHooks = TableInstance<ProductCategory> & {
  headers: Array<ColumnInstance<ProductCategory> & UseSortByColumnProps<ProductCategory>>
}

// ─── Main Component ───────────────────────────────────────────────────────────

export const ManageCategory = () => {
  const {company} = useLayout()

  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [data, setData] = useState<ProductCategory[]>([])
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [editCategoryData, setEditCategoryData] = useState<ProductCategory | undefined>(undefined)
  const [lastCompanyId, setLastCompanyId] = useState<string | null>(null)
  const [shouldRefreshCategories, setShouldRefreshCategories] = useState(true)

  // Pass both setters into columns so the edit button can use them
  const columns = useMemo(() => getCategoryColumns(setShowForm, setEditCategoryData), [])

  const debouncedSearchTerm = useDebounce(searchTerm, 150)

  const filteredData: ProductCategory[] = useMemo(() => {
    if (debouncedSearchTerm?.length) {
      return data.filter(
        (d) =>
          d.category_name.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
          d.abbreviation.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      )
    }
    return []
  }, [debouncedSearchTerm, data])

  const {getTableProps, getTableBodyProps, headers, rows, prepareRow} = useTable(
    {
      columns,
      data: debouncedSearchTerm?.length ? filteredData : data,
    },
    useSortBy
  ) as TableInstanceWithHooks

  const getAllCategories = useCallback(async () => {
    if (company?.value) {
      try {
        setIsError(false)
        setIsLoading(true)
        const res = await getCategories(company.value)
        res && setData(res)
        setLastCompanyId(company.value)
      } catch {
        setIsError(true)
      } finally {
        setIsLoading(false)
      }
    }
  }, [company])

  useEffect(() => {
    if (!showForm && shouldRefreshCategories) {
      getAllCategories()
      setEditCategoryData(undefined)
    }
  }, [getAllCategories, showForm, shouldRefreshCategories])

  // Separate effect to handle company changes
  useEffect(() => {
    if (company?.value && company.value !== lastCompanyId) {
      getAllCategories()
    }
  }, [company?.value, getAllCategories, lastCompanyId])

  return (
    <>
      {/* Toolbar Card */}
      <KTCard className='mb-5'>
        <div className='card-header border-0 justify-content-end'>
          <div className='card-toolbar'>
            <div className='d-flex gap-2' data-kt-user-table-toolbar='base'>
              <button
                type='button'
                className='btn p-0'
                style={{
                  backgroundColor: 'var(--bs-primary)',
                  color: 'var(--bs-primary-inverse)',
                  display: 'inline-flex',
                  height: '43.59px',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                }}
                onClick={() => {
                  setEditCategoryData(undefined)
                  setShowForm(true)
                }}
              >
                <span className='px-5'>Add Category</span>
                <span
                  style={{
                    color: '#fff',
                    backgroundColor: '#006060',
                    padding: '0 15px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    borderTopRightRadius: 'inherit',
                    borderBottomRightRadius: 'inherit',
                  }}
                >
                  <KTIcon iconName='plus' className='fs-2 text-white' />
                </span>
              </button>
            </div>
          </div>
        </div>
      </KTCard>

      {/* Table Card */}
      <KTCard>
        <>
          <div className='card-header border-0 pt-6'>
            <div className='card-title'>
              {/* begin::Search */}
              <div className='d-flex align-items-center position-relative my-1'>
                <KTIcon iconName='magnifier' className='fs-1 position-absolute ms-6' />
                <input
                  type='text'
                  data-kt-user-table-filter='search'
                  className='form-control form-control-solid w-250px ps-14'
                  placeholder='Search'
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              {/* end::Search */}
            </div>
          </div>

          <KTCardBody className='py-4'>
            {!isLoading && (
              <div className='table-responsive'>
                <table
                  className='table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer'
                  {...getTableProps()}
                >
                  <thead>
                    <tr className='text-start text-muted fw-bolder bg-light ps-4 fs-7 text-uppercase gs-0'>
                      {headers.map((column: SortableColumn) => (
                        <CustomHeaderColumn key={column.id} column={column} />
                      ))}
                    </tr>
                  </thead>
                  <tbody className='text-gray-600 text-center fw-bold' {...getTableBodyProps()}>
                    {rows.length > 0 ? (
                      rows.map((row: Row<ProductCategory>, i) => {
                        prepareRow(row)
                        return <CustomRow row={row} key={`row-${i}-${row.id}`} />
                      })
                    ) : (
                      <tr>
                        <td colSpan={3}>
                          <div className='d-flex text-center w-100 align-content-center justify-content-center'>
                            No matching records found
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
            {isLoading && <Loader isError={isError} />}
          </KTCardBody>

          <Modal
            className='modal-sticky modal-sticky-lg modal-sticky-bottom-right'
            role='dialog'
            data-backdrop='false'
            aria-hidden='true'
            tabIndex='-1'
            key={Date.now()}
            show={showForm}
            animation={true}
            size='lg'
            contentClassName='top-0'
          >
            <CreateEditCategory
              key={Date.now()}
              setEditCategory={setEditCategoryData}
              setShowForm={setShowForm}
              editCategoryData={editCategoryData}
              setShouldRefreshCategories={setShouldRefreshCategories}
            />
          </Modal>
        </>
      </KTCard>
    </>
  )
}

// ─── Table Sub-components ─────────────────────────────────────────────────────

type ColProps = {
  column: ColumnInstance<ProductCategory> & UseSortByColumnProps<ProductCategory>
}

export const CustomHeaderColumn: React.FC<ColProps> = ({column}) => (
  <>
    {column.Header && typeof column.Header === 'string' ? (
      <th {...column.getHeaderProps(column.getSortByToggleProps())} className='cursor-pointer'>
        {column.render('Header')}
        <span className='ms-1'>{column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}</span>
      </th>
    ) : (
      column.render('Header')
    )}
  </>
)

type RowProps = {
  row: Row<ProductCategory>
}

const CustomRow: React.FC<RowProps> = ({row}) => (
  <tr {...row.getRowProps()} className='bg-hover-light'>
    {row.cells.map((cell) => {
      return (
        <td
          {...cell.getCellProps()}
          className={clsx({'text-end min-w-100px': cell.column.id === 'actions'})}
        >
          {cell.render('Cell')}
        </td>
      )
    })}
  </tr>
)
